import { ensurePushSubscription } from '../hooks/usePushNotifications';

// Reusable push-permission ask: in-app explainer, then the system prompt
// (BrainSyncFCM bridge in the Android app, Web Push in the browser).
// Capped at one ask per 14 days. Callers pass a context such as
// 'first_save' | 'reminder' | 'onboarding'.

const LAST_ASK_KEY = 'bs_push_last_ask';
const ASK_INTERVAL_MS = 14 * 24 * 60 * 60 * 1000;
const NATIVE_TIMEOUT_MS = 120000;

let explainerHost = null;
let inFlight = null;

export function setPushExplainerHost(fn) {
    explainerHost = fn;
}

function nativeBridge() {
    const b = typeof window !== 'undefined' ? window.BrainSyncFCM : null;
    return b && typeof b.requestPermission === 'function' ? b : null;
}

// 'granted' | 'prompt' | 'denied' | 'blocked' | 'unsupported'
export function getPushPermissionStatus() {
    const native = nativeBridge();
    if (native) return native.getPermissionStatus();
    // Older app builds expose BrainSyncFCM without the permission API.
    if (window.BrainSyncFCM) return 'unsupported';
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        return 'unsupported';
    }
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'blocked';
    return 'prompt';
}

function recentlyAsked() {
    try {
        const last = parseInt(localStorage.getItem(LAST_ASK_KEY) || '0', 10);
        return Date.now() - last < ASK_INTERVAL_MS;
    } catch (_) {
        return true;
    }
}

function markAsked() {
    try { localStorage.setItem(LAST_ASK_KEY, String(Date.now())); } catch (_) {}
}

function askNative(native, context) {
    return new Promise((resolve) => {
        const done = (status) => {
            window.removeEventListener('brainsync-push-permission', onResult);
            clearTimeout(timer);
            resolve(status);
        };
        const onResult = (e) => done(e?.detail?.status || 'denied');
        const timer = setTimeout(() => done(native.getPermissionStatus()), NATIVE_TIMEOUT_MS);
        window.addEventListener('brainsync-push-permission', onResult);
        native.requestPermission(context);
    });
}

async function askBrowser() {
    const res = await ensurePushSubscription();
    if (res.ok) return 'granted';
    return Notification.permission === 'denied' ? 'blocked' : 'denied';
}

// Resolves to 'granted' | 'denied' | 'blocked' | 'unsupported' | 'dismissed' | 'skipped'.
async function run(context) {
    const status = getPushPermissionStatus();
    if (status === 'granted') {
        if (!nativeBridge()) await ensurePushSubscription();
        return 'granted';
    }
    if (status === 'unsupported' || status === 'blocked') return status;
    if (recentlyAsked() || !explainerHost) return 'skipped';

    markAsked();
    const accepted = await explainerHost(context);
    if (!accepted) return 'dismissed';

    const native = nativeBridge();
    return native ? askNative(native, context) : askBrowser();
}

export function requestPushPermission(context = 'unknown') {
    if (!inFlight) {
        inFlight = run(context).catch(() => 'denied').finally(() => { inFlight = null; });
    }
    return inFlight;
}
